import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Building2, Pencil, Trash2, Plus, Users } from "lucide-react";

export default function AdminVendors() {
    const [vendors, setVendors] = useState([]);
    const [unassigned, setUnassigned] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = new, object = rename
    const [name, setName] = useState("");

    const [brokersOpen, setBrokersOpen] = useState(null); // vendor being managed
    const [newBrokerOpen, setNewBrokerOpen] = useState(false);
    const [newBrokerName, setNewBrokerName] = useState("");
    const [newBrokerEmail, setNewBrokerEmail] = useState("");
    const [creatingBroker, setCreatingBroker] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/vendors");
            setVendors(data.vendors);
            setUnassigned(data.unassigned_brokers);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const allBrokers = useMemo(
        () => [
            ...vendors.flatMap((v) => v.brokers.map((b) => ({ ...b, vendor_id: v.id }))),
            ...unassigned.map((b) => ({ ...b, vendor_id: null })),
        ],
        [vendors, unassigned]
    );

    const openNew = () => {
        setEditing(null);
        setName("");
        setFormOpen(true);
    };

    const openRename = (v) => {
        setEditing(v);
        setName(v.name);
        setFormOpen(true);
    };

    const submitForm = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setSaving(true);
        try {
            if (editing) {
                await api.patch(`/admin/vendors/${editing.id}`, { name: name.trim() });
                toast.success("Vendor renamed");
            } else {
                await api.post("/admin/vendors", { name: name.trim() });
                toast.success("Vendor created");
            }
            setFormOpen(false);
            await load();
        } catch (e2) {
            toast.error(formatApiError(e2));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (v) => {
        if (
            !window.confirm(
                `Delete vendor "${v.name}"? Any linked brokers will be unassigned. This cannot be undone.`
            )
        )
            return;
        try {
            await api.delete(`/admin/vendors/${v.id}`);
            toast.success(`${v.name} deleted`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const createBroker = async (e) => {
        e.preventDefault();
        if (!brokersOpen || !newBrokerName.trim() || !newBrokerEmail.trim()) return;
        setCreatingBroker(true);
        try {
            await api.post(`/admin/vendors/${brokersOpen.id}/brokers`, {
                name: newBrokerName.trim(),
                email: newBrokerEmail.trim(),
            });
            toast.success(`Invite sent to ${newBrokerEmail.trim()}`);
            setNewBrokerName("");
            setNewBrokerEmail("");
            setNewBrokerOpen(false);
            await load();
        } catch (e2) {
            toast.error(formatApiError(e2));
        } finally {
            setCreatingBroker(false);
        }
    };

    const toggleBroker = async (broker, vendorId) => {
        const nextVendorId = broker.vendor_id === vendorId ? null : vendorId;
        try {
            await api.put(`/admin/brokers/${broker.id}/vendor`, {
                vendor_id: nextVendorId,
            });
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <>
            <PageHeader
                eyebrow="Broker network"
                title="Vendors"
                description="Group broker logins under a vendor company. Brokers sharing a vendor see each other's products and applications."
                testid="admin-vendors-header"
                action={
                    <Button
                        onClick={openNew}
                        data-testid="vendor-new"
                        className="rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 h-11 px-5 font-semibold"
                    >
                        <Plus size={16} className="mr-2" /> New vendor
                    </Button>
                }
            />

            <div className="px-6 lg:px-12 py-8 space-y-6">
                <div className="grid sm:grid-cols-3 gap-5">
                    <div className="rounded-2xl bg-white shadow-soft p-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                            Vendors
                        </div>
                        <div className="font-display text-4xl text-navy-700">
                            {vendors.length}
                        </div>
                    </div>
                    <div className="rounded-2xl bg-white shadow-soft p-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                            Brokers assigned
                        </div>
                        <div className="font-display text-4xl text-navy-700">
                            {allBrokers.length - unassigned.length}
                        </div>
                    </div>
                    <div className="rounded-2xl bg-navy-700 text-white shadow-soft p-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-500 mb-2">
                            Unassigned brokers
                        </div>
                        <div className="font-display text-4xl">{unassigned.length}</div>
                        <div className="text-navy-50/60 text-sm mt-1">
                            scoped to their own submissions only
                        </div>
                    </div>
                </div>

                <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                    <Table data-testid="vendors-table">
                        <TableHeader>
                            <TableRow className="bg-cream hover:bg-cream">
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Vendor
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Brokers
                                </TableHead>
                                <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Created
                                </TableHead>
                                <TableHead className="text-right text-navy-700 uppercase text-xs tracking-[0.2em]">
                                    Actions
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {!loading && vendors.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center text-navy-500 py-10">
                                        No vendors yet — create one to start grouping broker logins.
                                    </TableCell>
                                </TableRow>
                            )}
                            {vendors.map((v) => (
                                <TableRow key={v.id} data-testid={`vendor-row-${v.id}`}>
                                    <TableCell className="text-navy-700 font-medium">
                                        <div className="flex items-center gap-2">
                                            <Building2 size={16} className="text-navy-400" />
                                            {v.name}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {v.brokers.length === 0 ? (
                                            <span className="text-navy-400 text-sm">— none —</span>
                                        ) : (
                                            <div className="flex flex-wrap gap-1.5">
                                                {v.brokers.map((b) => (
                                                    <Badge
                                                        key={b.id}
                                                        className="bg-navy-50 text-navy-700 border-navy-100 border font-normal"
                                                    >
                                                        {b.name}
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-navy-500">
                                        {v.created_at
                                            ? new Date(v.created_at).toLocaleDateString()
                                            : "—"}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1.5">
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                data-testid={`vendor-brokers-${v.id}`}
                                                onClick={() => setBrokersOpen(v)}
                                                className="h-9 w-9 rounded-full border-navy-100"
                                                title="Manage brokers"
                                            >
                                                <Users size={14} />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                data-testid={`vendor-rename-${v.id}`}
                                                onClick={() => openRename(v)}
                                                className="h-9 w-9 rounded-full border-navy-100"
                                                title="Rename"
                                            >
                                                <Pencil size={14} />
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                data-testid={`vendor-delete-${v.id}`}
                                                onClick={() => remove(v)}
                                                className="h-9 w-9 rounded-full border-red-100 text-red-600 hover:bg-red-50"
                                                title="Delete"
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Create / rename dialog */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent data-testid="vendor-form-dialog">
                    <form onSubmit={submitForm}>
                        <DialogHeader>
                            <DialogTitle>{editing ? "Rename vendor" : "New vendor"}</DialogTitle>
                            <DialogDescription>
                                {editing
                                    ? "Update this vendor's display name."
                                    : "Create a vendor company that broker logins can be grouped under."}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                Vendor name
                            </Label>
                            <Input
                                autoFocus
                                required
                                data-testid="vendor-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="submit"
                                disabled={saving || !name.trim()}
                                data-testid="vendor-form-submit"
                                className="rounded-full bg-navy-700 hover:bg-navy-800 text-white"
                            >
                                {saving ? "Saving…" : editing ? "Save" : "Create vendor"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Manage brokers dialog */}
            <Dialog
                open={!!brokersOpen}
                onOpenChange={(o) => {
                    if (!o) {
                        setBrokersOpen(null);
                        setNewBrokerOpen(false);
                        setNewBrokerName("");
                        setNewBrokerEmail("");
                    }
                }}
            >
                <DialogContent data-testid="vendor-brokers-dialog">
                    <DialogHeader>
                        <DialogTitle>Brokers — {brokersOpen?.name}</DialogTitle>
                        <DialogDescription>
                            Check a broker to assign them to this vendor. A broker can only
                            belong to one vendor at a time — checking them here moves them
                            off any other vendor they're currently on.
                        </DialogDescription>
                    </DialogHeader>

                    {newBrokerOpen ? (
                        <form
                            onSubmit={createBroker}
                            className="rounded-xl bg-cream border border-navy-100/70 p-4 space-y-3"
                        >
                            <div>
                                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                    Name
                                </Label>
                                <Input
                                    autoFocus
                                    required
                                    data-testid="new-broker-name"
                                    value={newBrokerName}
                                    onChange={(e) => setNewBrokerName(e.target.value)}
                                    className="mt-1.5 h-10 bg-white"
                                />
                            </div>
                            <div>
                                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                    Email
                                </Label>
                                <Input
                                    type="email"
                                    required
                                    data-testid="new-broker-email"
                                    value={newBrokerEmail}
                                    onChange={(e) => setNewBrokerEmail(e.target.value)}
                                    className="mt-1.5 h-10 bg-white"
                                />
                            </div>
                            <div className="text-xs text-navy-500">
                                We'll email them a 7-day activation link to set their own
                                password.
                            </div>
                            <div className="flex gap-2 justify-end">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setNewBrokerOpen(false)}
                                    className="rounded-full"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    size="sm"
                                    disabled={
                                        creatingBroker ||
                                        !newBrokerName.trim() ||
                                        !newBrokerEmail.trim()
                                    }
                                    data-testid="new-broker-submit"
                                    className="rounded-full bg-navy-700 hover:bg-navy-800 text-white"
                                >
                                    {creatingBroker ? "Sending…" : "Send invite"}
                                </Button>
                            </div>
                        </form>
                    ) : (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setNewBrokerOpen(true)}
                            data-testid="new-broker-open"
                            className="rounded-full border-navy-700 text-navy-700 self-start"
                        >
                            <Plus size={14} className="mr-1.5" /> Create broker
                        </Button>
                    )}

                    <div className="py-2 max-h-80 overflow-y-auto space-y-1">
                        {allBrokers.length === 0 && (
                            <div className="text-sm text-navy-500 py-6 text-center">
                                No broker accounts yet.
                            </div>
                        )}
                        {allBrokers.map((b) => {
                            const checked = brokersOpen && b.vendor_id === brokersOpen.id;
                            return (
                                <label
                                    key={b.id}
                                    className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-cream cursor-pointer"
                                >
                                    <Checkbox
                                        checked={checked}
                                        onCheckedChange={() =>
                                            brokersOpen && toggleBroker(b, brokersOpen.id)
                                        }
                                        data-testid={`vendor-broker-check-${b.id}`}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-navy-700 font-medium truncate">
                                            {b.name}
                                        </div>
                                        <div className="text-xs text-navy-500 truncate">
                                            {b.email}
                                        </div>
                                    </div>
                                    {b.vendor_id && b.vendor_id !== brokersOpen?.id && (
                                        <span className="text-[10px] uppercase tracking-widest text-gold-600">
                                            on another vendor
                                        </span>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setBrokersOpen(null)}
                            className="rounded-full"
                        >
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
